import { Link2, Plus, Search } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useExternalTaskRuleListQuery } from '@/services/api/firmsService';

import { BackfillPanel } from './components/BackfillPanel';
import { EmptyState } from './components/EmptyState';
import { RuleCard } from './components/RuleCard';

interface TaskLinkingRulesPageProps {
  firmId: string;
}

export const TaskLinkingRulesPage: React.FC<TaskLinkingRulesPageProps> = ({
  firmId,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: rules = [], isLoading } = useExternalTaskRuleListQuery({
    firmId,
  });

  const filteredRules = rules.filter(
    (rule) =>
      rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rule.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const enabledRulesCount = rules.filter(
    (rule) => rule.status === 'ENABLED',
  ).length;

  const handleCreateRule = () => {
    navigate(`/firms/${firmId}/task-linking/new`);
  };

  const handleEditRule = (ruleId: string) => {
    navigate(`/firms/${firmId}/task-linking/${ruleId}`);
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
              <Link2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">
                Task Linking Rules
              </h1>
              <p className="text-sm text-slate-500">
                Automatically connect Filevine tasks with internal tasks
              </p>
            </div>
          </div>
        </div>
        <Button onClick={handleCreateRule} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Rule
        </Button>
      </div>

      {/* Stats Summary */}
      {rules.length > 0 && (
        <div className="flex items-center gap-6 rounded-xl border border-slate-200 bg-white px-5 py-4">
          <div>
            <p className="text-2xl font-semibold text-slate-900">
              {rules.length}
            </p>
            <p className="text-xs font-medium text-slate-500">Total Rules</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <p className="text-2xl font-semibold text-emerald-600">
              {enabledRulesCount}
            </p>
            <p className="text-xs font-medium text-slate-500">Enabled</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <p className="text-2xl font-semibold text-slate-400">
              {rules.length - enabledRulesCount}
            </p>
            <p className="text-xs font-medium text-slate-500">Disabled</p>
          </div>
        </div>
      )}

      {/* Main content with sidebar */}
      <div className="grid gap-6 lg:grid-cols-[1fr,320px]">
        {/* Rules section */}
        <div className="space-y-4">
          {rules.length === 0 ? (
            <EmptyState onCreateRule={handleCreateRule} />
          ) : (
            <>
              {/* Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search rules..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-100"
                />
              </div>

              {/* Rules Grid */}
              {filteredRules.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-8 text-center">
                  <p className="text-sm text-slate-500">
                    No rules match your search.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {filteredRules.map((rule) => (
                    <RuleCard
                      key={rule.id}
                      rule={rule}
                      onEdit={() => handleEditRule(rule.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar with backfill panel */}
        <div className="space-y-4">
          <BackfillPanel
            firmId={firmId}
            enabledRulesCount={enabledRulesCount}
          />
        </div>
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-100" />
        <div className="space-y-2">
          <div className="h-6 w-40 animate-pulse rounded-lg bg-slate-100" />
          <div className="h-4 w-64 animate-pulse rounded-lg bg-slate-100" />
        </div>
      </div>
      <div className="h-10 w-28 animate-pulse rounded-lg bg-slate-100" />
    </div>

    {/* Stats skeleton */}
    <div className="h-20 animate-pulse rounded-xl bg-slate-100" />

    {/* Content skeleton */}
    <div className="grid gap-6 lg:grid-cols-[1fr,320px]">
      <div className="space-y-4">
        <div className="h-10 w-80 animate-pulse rounded-xl bg-slate-100" />
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-44 animate-pulse rounded-xl bg-slate-100"
            />
          ))}
        </div>
      </div>
      <div className="h-64 animate-pulse rounded-xl bg-slate-100" />
    </div>
  </div>
);
